/* eslint-disable eqeqeq */
/* eslint-disable jsx-a11y/anchor-is-valid */

import React, { useEffect, useMemo, useState } from "react";
import { Table, Modal, Button, Form } from "react-bootstrap";
import ReactPaginate from "react-paginate";
import { PropagateLoader } from "react-spinners";
import { toast } from "react-toastify";
import { useTranslate } from "../hooks/LanguageProvider";
import {
  fetchCarburantList,
  updateCarburant,
  deleteCarburant,
  createCarburant,
} from "../utilities/carburantApi";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

/**
 * ✅ CHANGE ONLY THIS if your backend route differs
 * Example:
 *  - /api/geop/carburant/import/:id_user
 *  - /api/carburant/import/:id_user
 */
const IMPORT_URL = (id_user: number) =>
  `${backendUrl}/api/carburant/import/${id_user}`;

const FUEL_TYPES = [
  { value: "DIESEL", label: "Diesel" },
  { value: "ESSENCE", label: "Essence" },
  { value: "GPL", label: "GPL" },
  { value: "ELECTRIQUE", label: "Electrique" },
] as const;

type SortDir = "ASC" | "DESC";
type FilterKey =
  | "vehicule"
  | "employe"
  | "utilisateur"
  | "station"
  | "carburant"
  | "date";

type SortColumn =
  | "vehicule"
  | "employe"
  | "utilisateur"
  | "volume"
  | "total"
  | "carburant"
  | "recu"
  | "date"
  | "station";

export interface CarburantRow {
  id: number;
  vehicule?: string | null;
  employe?: string | null;
  utilisateur?: string | null;
  volume?: number | null;
  total?: number | null;
  carburant?: string | null;
  recu?: string | null;
  date?: string | null;
  station?: string | null;
  id_user?: number | null;
  id_vehicule?: number | null;
}

type UserOpt = { id_user: number; username_user: string };
type VehOpt = { id_vehicule: number; immatriculation_vehicule: string };
type DriverOpt = {
  id_conducteur: number;
  nom_conducteur: string;
  prenom_conducteur: string;
  code_conducteur?: string;
};

function formatDateCell(v?: string | null) {
  if (!v) return "-";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);

  // Same style as your "Reçu de carburant" (dd/mm/yyyy hh:mm:ss in FR)
  return d.toLocaleString("fr-FR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}
function toNumberInputValue(v: number | null | undefined) {
  return v === null || v === undefined ? "" : String(v);
}

function parseNumberInput(raw: string) {
  const s = String(raw ?? "").replace(",", "."); // keep your old behavior (comma allowed)
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

// optional: block e/E/+/- in number inputs
function blockInvalidNumberKeys(e: React.KeyboardEvent<HTMLInputElement>) {
  if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault();
}

function safeText(v: any) {
  if (v === null || v === undefined || v === "") return "-";
  return String(v);
}
function safeNumber(v: any) {
  if (v === null || v === undefined || v === "") return "-";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}
function toInputDateTime(value: string | null | undefined) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (x: number) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}
function toServerDateTime(value: string) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}
function fuelLabel(v: string | null | undefined) {
  if (!v) return "-";
  const found = FUEL_TYPES.find((x) => x.value === v);
  return found ? found.label : v;
}

/* ===================== NUMERIC INPUT ===================== */
function sanitizeDecimalInput(raw: string) {
  let v = (raw ?? "").replace(",", ".").replace(/[^\d.]/g, "");
  const parts = v.split(".");
  if (parts.length > 2) v = parts[0] + "." + parts.slice(1).join("");
  return v;
}
function hasLetters(raw: string) {
  return /[a-zA-Z]/.test(raw ?? "");
}
function toastNombreFr() {
  toast.warning("Veuillez saisir un nombre.", {
    position: "bottom-right",
    autoClose: 1800,
  });
}

/* ===================== SearchableDropdown ===================== */
type DDOpt = { value: number; label: string };

function SearchableDropdown({
  label,
  placeholder,
  options,
  value,
  onChange,
  disabled,
}: {
  label: string;
  placeholder: string;
  options: DDOpt[];
  value: number | null;
  onChange: (val: number | null) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selectedLabel = options.find((o) => o.value === value)?.label || "";

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const el = e.target as HTMLElement;
      if (!el.closest(".gs-dd-root")) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (open) setQuery("");
  }, [open]);

  return (
    <div className="mb-3 gs-dd-root" style={{ position: "relative" }}>
      <Form.Label>{label}</Form.Label>

      <div className="input-group">
        <Form.Control
          type="text"
          placeholder={placeholder}
          value={open ? query : selectedLabel}
          disabled={disabled}
          onFocus={() => !disabled && setOpen(true)}
          onClick={() => !disabled && setOpen(true)}
          onChange={(e) => setQuery(e.target.value)}
        />
        <Button
          variant="light"
          className="border"
          disabled={disabled}
          onClick={() => !disabled && setOpen((v) => !v)}
        >
          <i className="las la-search" />
        </Button>
      </div>

      {open && !disabled && (
        <div
          className="dropdown-menu show w-100"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            zIndex: 9999,
            maxHeight: 260,
            overflowY: "auto",
          }}
          onMouseDown={(e) => e.preventDefault()}
        >
          {filtered.length ? (
            filtered.map((o) => (
              <button
                key={o.value}
                type="button"
                className={`dropdown-item ${o.value === value ? "active" : ""}`}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
              >
                {o.label}
              </button>
            ))
          ) : (
            <div className="px-3 py-2 text-muted" style={{ fontSize: 13 }}>
              No results
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ===================== View Modal ===================== */
function ViewCarburantModal({
  show,
  onHide,
  row,
  translate,
}: {
  show: boolean;
  onHide: () => void;
  row: CarburantRow | null;
  translate: (k: string) => string;
}) {
  const formatDate = (v?: string | null) => {
    if (!v) return "-";
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? v : d.toLocaleString();
  };

  const Line = ({ label, value }: { label: string; value: any }) => (
    <div className="d-flex justify-content-between align-items-center mb-3">
      <span style={{ color: "#6c757d", fontSize: 14 }}>{label}</span>
      <span style={{ fontWeight: 600, fontSize: 15 }}>{value}</span>
    </div>
  );

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          ⛽ {translate("Reçu de carburant") || "Reçu carburant"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {!row ? (
          <div className="text-muted">-</div>
        ) : (
          <div style={{ background: "#f8f9fa", borderRadius: 10, padding: 16 }}>
            <Line
              label={translate("Vehicle") || "Véhicule"}
              value={safeText(row.vehicule)}
            />
            <Line
              label={translate("Employee") || "Employée"}
              value={safeText(row.employe)}
            />
            <Line
              label={translate("User") || "Utilisateur"}
              value={safeText(row.utilisateur)}
            />
            <Line
              label={translate("Station") || "Station"}
              value={safeText(row.station)}
            />

            <hr />

            <Line
              label={translate("Volume") || "Volume"}
              value={`${safeNumber(row.volume)} L`}
            />
            <Line
              label={translate("Carburant") || "Carburant"}
              value={fuelLabel(row.carburant)}
            />
            <Line
              label={translate("Total") || "Total"}
              value={`${safeNumber(row.total)} DA`}
            />
            <Line
              label={translate("Receipt") || "Reçu"}
              value={safeText(row.recu)}
            />
            <Line
              label={translate("Date") || "Date"}
              value={formatDate(row.date)}
            />
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>
          {translate("Close") || "Fermer"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

/* ===================== Edit Modal ===================== */
function EditCarburantModal({
  show,
  onHide,
  row,
  translate,
  onSaved,
}: {
  show: boolean;
  onHide: () => void;
  row: CarburantRow | null;
  translate: (k: string) => string;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<CarburantRow | null>(row);
  const [saving, setSaving] = useState(false);
  //
  const [driversOptions, setDriversOptions] = useState<DriverOpt[]>([]);
  const [selectedDriverId, setSelectedDriverId] = useState<number | null>(null);

  const driversDD: DDOpt[] = useMemo(
    () =>
      driversOptions.map((d) => ({
        value: d.id_conducteur,
        label: `${d.nom_conducteur} ${d.prenom_conducteur}`,
      })),
    [driversOptions],
  );

  useEffect(() => {
    setForm(row ? { ...row } : null);
  }, [row]);
  //
  useEffect(() => {
    if (!show || !row?.id_user) {
      setDriversOptions([]);
      setSelectedDriverId(null);
      return;
    }

    fetch(`${backendUrl}/api/driver/options/${row.id_user}`, {
      credentials: "include",
    })
      .then((r) => r.json())
      .then((rows) => {
        const list = Array.isArray(rows) ? rows : [];
        setDriversOptions(list);

        // auto-select current employee if possible
        const current = String(row.employe || "")
          .trim()
          .toLowerCase();
        const found = list.find(
          (d) =>
            `${d.nom_conducteur} ${d.prenom_conducteur}`
              .trim()
              .toLowerCase() === current,
        );
        setSelectedDriverId(found?.id_conducteur ?? null);
      })
      .catch((e) => {
        console.error(e);
        setDriversOptions([]);
        setSelectedDriverId(null);
      });
  }, [show, row?.id_user, row?.employe]);

  const setField = (key: keyof CarburantRow, value: any) => {
    if (!form) return;
    setForm({ ...form, [key]: value });
  };

  const handleSave = async () => {
    if (!form) return;

    if (
      form.volume === null ||
      form.volume === undefined ||
      Number(form.volume) <= 0
    ) {
      toast.error("Veuillez saisir un volume valide.", {
        position: "bottom-right",
        autoClose: 2000,
      });
      return;
    }
    if (
      form.total === null ||
      form.total === undefined ||
      Number(form.total) <= 0
    ) {
      toast.error("Veuillez saisir un total valide.", {
        position: "bottom-right",
        autoClose: 2000,
      });
      return;
    }

    try {
      setSaving(true);

      const payload = {
        employe: form.employe ?? null,
        volume:
          form.volume === null || form.volume === undefined
            ? null
            : Number(form.volume),
        total:
          form.total === null || form.total === undefined
            ? null
            : Number(form.total),
        station: form.station ?? null,
        recu: form.recu ?? null,
        carburant: form.carburant ?? null,
        date: form.date ? new Date(form.date).toISOString() : null,
      };

      await updateCarburant(form.id, payload);

      toast.success(translate("Saved") || "Saved", {
        position: "bottom-right",
        autoClose: 1500,
      });
      onHide();
      onSaved();
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || translate("Save failed") || "Save failed", {
        position: "bottom-right",
        autoClose: 2500,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {translate("Modifier le reçu") || "Modifier le reçu"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {!form ? (
          <div style={{ color: "#999" }}>-</div>
        ) : (
          <Form>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Form.Label>{translate("User") || "Utilisateur"}</Form.Label>
                <Form.Control value={safeText(form.utilisateur)} disabled />
              </div>

              <div className="col-md-6 mb-3">
                <Form.Label>{translate("Vehicle") || "Véhicule"}</Form.Label>
                <Form.Control value={safeText(form.vehicule)} disabled />
              </div>

              <div className="col-md-6">
                <SearchableDropdown
                  label={translate("Employee") || "Employée"}
                  placeholder={translate("Search...") || "Rechercher..."}
                  options={driversDD}
                  value={selectedDriverId}
                  disabled={!row?.id_user || !driversDD.length}
                  onChange={(val) => {
                    setSelectedDriverId(val);

                    const found = driversOptions.find(
                      (d) => d.id_conducteur === val,
                    );
                    setField(
                      "employe",
                      found
                        ? `${found.nom_conducteur} ${found.prenom_conducteur}`
                        : "",
                    );
                  }}
                />
              </div>

              <div className="col-md-6 mb-3">
                <Form.Label>
                  {translate("Volume") || "Volume de carburant"}
                </Form.Label>
                <div className="input-group">
                  <Form.Control
                    type="text"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={form.volume ?? ""}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (hasLetters(raw)) toastNombreFr();
                      const clean = sanitizeDecimalInput(raw);
                      setField("volume", clean === "" ? null : Number(clean));
                    }}
                    onPaste={(e) => {
                      const pasted = e.clipboardData.getData("text");
                      if (hasLetters(pasted)) toastNombreFr();
                    }}
                  />

                  <Form.Select
                    value={form.carburant ?? ""}
                    onChange={(e) => setField("carburant", e.target.value)}
                    style={{ maxWidth: 180 }}
                  >
                    <option value="">
                      {translate("Carburant") || "Carburant"}
                    </option>
                    {FUEL_TYPES.map((f) => (
                      <option key={f.value} value={f.value}>
                        {f.label}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              </div>

              <div className="col-md-6 mb-3">
                <Form.Label>{translate("Total") || "Total"}</Form.Label>
                <Form.Control
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={form.total ?? ""}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (hasLetters(raw)) toastNombreFr();
                    const clean = sanitizeDecimalInput(raw);
                    setField("total", clean === "" ? null : Number(clean));
                  }}
                  onPaste={(e) => {
                    const pasted = e.clipboardData.getData("text");
                    if (hasLetters(pasted)) toastNombreFr();
                  }}
                />
              </div>

              <div className="col-md-6 mb-3">
                <Form.Label>
                  {translate("Numéro de série du reçu") || "Reçu"}
                </Form.Label>
                <Form.Control
                  value={safeText(form.recu) === "-" ? "" : String(form.recu)}
                  onChange={(e) => setField("recu", e.target.value)}
                />
              </div>

              <div className="col-md-6 mb-3">
                <Form.Label>{translate("Station") || "Station"}</Form.Label>
                <Form.Control
                  value={
                    safeText(form.station) === "-" ? "" : String(form.station)
                  }
                  onChange={(e) => setField("station", e.target.value)}
                />
              </div>

              <div className="col-md-6 mb-3">
                <Form.Label>{translate("Date") || "Date"}</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={toInputDateTime(form.date || null)}
                  onChange={(e) =>
                    setField("date", toServerDateTime(e.target.value))
                  }
                />
              </div>
            </div>
          </Form>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>
          {translate("Close") || "Close"}
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={saving || !form}
        >
          {saving
            ? translate("Saving...") || "Saving..."
            : translate("Save") || "Sauvegarder"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

/* ===================== Add Modal ===================== */
function AddCarburantModal({
  show,
  onHide,
  translate,
  loggedUserId,
  onCreated,
}: {
  show: boolean;
  onHide: () => void;
  translate: (k: string) => string;
  loggedUserId: number;
  onCreated: (createdForUserId: number) => void; // ✅ changed
}) {
  const [saving, setSaving] = useState(false);

  const [usersOptions, setUsersOptions] = useState<UserOpt[]>([]);
  const [vehiclesOptions, setVehiclesOptions] = useState<VehOpt[]>([]);

  const [driversOptions, setDriversOptions] = useState<DriverOpt[]>([]);
  const [selectedDriverId, setSelectedDriverId] = useState<number | null>(null);

  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  const [selectedVehicleId, setSelectedVehicleId] = useState<number | null>(
    null,
  );

  const [employe, setEmploye] = useState<string>("");
  const [volume, setVolume] = useState<number | null>(null);
  const [carburant, setCarburant] = useState<string>("");
  const [total, setTotal] = useState<number | null>(null);
  const [recu, setRecu] = useState<string>("");
  const [station, setStation] = useState<string>("");
  const [date, setDate] = useState<string | null>(new Date().toISOString());

  const usersDD: DDOpt[] = useMemo(
    () =>
      usersOptions.map((u) => ({ value: u.id_user, label: u.username_user })),
    [usersOptions],
  );

  const vehiclesDD: DDOpt[] = useMemo(
    () =>
      vehiclesOptions.map((v) => ({
        value: v.id_vehicule,
        label: v.immatriculation_vehicule,
      })),
    [vehiclesOptions],
  );
  const driversDD: DDOpt[] = useMemo(
    () =>
      driversOptions.map((d) => ({
        value: d.id_conducteur,
        label: `${d.nom_conducteur} ${d.prenom_conducteur}`,
      })),
    [driversOptions],
  );

  useEffect(() => {
    if (!show) return;

    setSelectedUserId(null); // force user selection

    setSelectedVehicleId(null);
    setVehiclesOptions([]);

    setEmploye("");
    setVolume(null);
    setCarburant("");
    setTotal(null);
    setRecu("");
    setStation("");
    setDate(new Date().toISOString());

    // ⚠️ if you want to list ALL users, your backend should return them.
    // currently you call /options/{loggedUserId} (maybe returns same user only)
    fetch(`${backendUrl}/api/users/options/${loggedUserId}`, {
      credentials: "include",
    })
      .then((r) => r.json())
      .then((rows) => setUsersOptions(Array.isArray(rows) ? rows : []))
      .catch((e) => {
        console.error(e);
        setUsersOptions([]);
      });
  }, [show, loggedUserId]);

  useEffect(() => {
    if (!show || !selectedUserId) return;

    fetch(`${backendUrl}/api/vehicle/options/${selectedUserId}`, {
      credentials: "include",
    })
      .then((r) => r.json())
      .then((rows) => {
        const list = Array.isArray(rows) ? rows : [];
        setVehiclesOptions(list);
        setSelectedVehicleId(null); // ✅ user must choose
      })
      .catch((e) => {
        console.error(e);
        setVehiclesOptions([]);
        setSelectedVehicleId(null);
      });
  }, [show, selectedUserId]);
  ///
  useEffect(() => {
    if (!show || !selectedUserId) return;

    // reset first
    setDriversOptions([]);
    setSelectedDriverId(null);

    fetch(`${backendUrl}/api/driver/options/${selectedUserId}`, {
      credentials: "include",
    })
      .then((r) => r.json())
      .then((rows) => {
        const list = Array.isArray(rows) ? rows : [];
        setDriversOptions(list);
        setSelectedDriverId(null); // ✅ user must choose
        setEmploye("");
      })
      .catch((e) => {
        console.error(e);
        setDriversOptions([]);
        setSelectedDriverId(null);
      });
  }, [show, selectedUserId]);

  /*const handleCreate = async () => {
    try {
      if (!selectedUserId) return toast.error(translate("Select user") || "Select user");
      if (!selectedVehicleId) return toast.error(translate("Select vehicle") || "Select vehicle");

      if (volume === null || volume === undefined || Number(volume) <= 0) {
        toast.error("Veuillez saisir un volume valide.", {
          position: "bottom-right",
          autoClose: 2000,
        });
        return;
      }
      if (total === null || total === undefined || Number(total) <= 0) {
        toast.error("Veuillez saisir un total valide.", {
          position: "bottom-right",
          autoClose: 2000,
        });
        return;
      }
      if (!carburant) {
  toast.error("Veuillez choisir un carburant.", { position: "bottom-right", autoClose: 2000 });
  return;
}


      setSaving(true);

     const payload = {
  id_user: selectedUserId, // ✅ NOT loggedUserId
  id_vehicule: selectedVehicleId,
  employe: employe || null,
  volume: volume === null ? null : Number(volume),
  carburant: carburant || null,
  total: total === null ? null : Number(total),
  recu: recu || null,
  station: station || null,
  date: date ? new Date(date).toISOString() : null,
};


      await createCarburant(payload);

      toast.success(translate("Created") || "Created", {
        position: "bottom-right",
        autoClose: 1500,
      });

      onHide();
      onCreated(selectedUserId); // ✅ tell parent which user created for
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || "Create failed", {
        position: "bottom-right",
        autoClose: 2500,
      });
    } finally {
      setSaving(false);
    }
  };*/
  const handleCreate = async () => {
    try {
      if (!selectedUserId)
        return toast.error(translate("Select user") || "Select user");
      if (!selectedVehicleId)
        return toast.error(translate("Select vehicle") || "Select vehicle");

      if (!volume || Number(volume) <= 0) return toast.error("Volume invalide");
      if (!total || Number(total) <= 0) return toast.error("Total invalide");

      const fuelToSend = carburant && carburant.trim() ? carburant : "DIESEL";

      const payload: any = {
        id_user: selectedUserId,
        id_vehicule: selectedVehicleId,
        employe: employe || null,
        volume: Number(volume),
        carburant: fuelToSend,
        total: Number(total),
        recu: recu || null,
        station: station || null,
        date: date ? new Date(date).toISOString() : null,
      };

      console.log("CREATE payload =>", payload); // ✅ HERE (after payload)

      setSaving(true);
      await createCarburant(payload);

      toast.success(translate("Created") || "Created", {
        position: "bottom-right",
        autoClose: 1500,
      });

      onHide();
      onCreated(selectedUserId);
    } catch (e: any) {
      console.error(e);
      const msg = e?.response?.data?.message || e?.message || "Create failed";
      toast.error(msg, { position: "bottom-right", autoClose: 4000 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {translate("Ajouter un reçu") || "Ajouter un reçu"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          <div className="row">
            <div className="col-md-6">
              <SearchableDropdown
                label={translate("User") || "Utilisateur"}
                placeholder={translate("Search...") || "Rechercher..."}
                options={usersDD}
                value={selectedUserId}
                onChange={(val) => {
                  setSelectedUserId(val);

                  // reset vehicles
                  setVehiclesOptions([]);
                  setSelectedVehicleId(null);

                  // reset drivers
                  setDriversOptions([]);
                  setSelectedDriverId(null);

                  // ✅ reset employe string
                  setEmploye("");
                }}
              />
            </div>

            <div className="col-md-6">
              <SearchableDropdown
                label={translate("Vehicle") || "Véhicule"}
                placeholder={translate("Search...") || "Rechercher..."}
                options={vehiclesDD}
                value={selectedVehicleId}
                disabled={!selectedUserId || !vehiclesDD.length} // ✅ add !selectedUserId
                onChange={(val) => setSelectedVehicleId(val)}
              />
            </div>

            <div className="col-md-6">
              <SearchableDropdown
                label={translate("Employee") || "Employée"}
                placeholder={translate("Search...") || "Rechercher..."}
                options={driversDD}
                value={selectedDriverId}
                disabled={!selectedUserId || !driversDD.length} // ✅ keep this
                onChange={(val) => {
                  setSelectedDriverId(val);

                  const found = driversOptions.find(
                    (d) => d.id_conducteur === val,
                  );
                  setEmploye(
                    found
                      ? `${found.nom_conducteur} ${found.prenom_conducteur}`
                      : "",
                  );
                }}
              />
            </div>

            <div className="col-md-6 mb-3">
              <Form.Label>{translate("Volume") || "Volume"}</Form.Label>
              <div className="input-group">
                <Form.Control
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={volume ?? ""}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (hasLetters(raw)) toastNombreFr();
                    const clean = sanitizeDecimalInput(raw);
                    setVolume(clean === "" ? null : Number(clean));
                  }}
                  onPaste={(e) => {
                    const pasted = e.clipboardData.getData("text");
                    if (hasLetters(pasted)) toastNombreFr();
                  }}
                />
                <Form.Select
                  value={carburant}
                  onChange={(e) => setCarburant(e.target.value)}
                  style={{ maxWidth: 180 }}
                >
                  <option value="">
                    {translate("Carburant") || "Carburant"}
                  </option>
                  {FUEL_TYPES.map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </Form.Select>
              </div>
            </div>

            <div className="col-md-6 mb-3">
              <Form.Label>{translate("Total") || "Total"}</Form.Label>
              <Form.Control
                type="text"
                inputMode="decimal"
                placeholder="0.00"
                value={total ?? ""}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (hasLetters(raw)) toastNombreFr();
                  const clean = sanitizeDecimalInput(raw);
                  setTotal(clean === "" ? null : Number(clean));
                }}
                onPaste={(e) => {
                  const pasted = e.clipboardData.getData("text");
                  if (hasLetters(pasted)) toastNombreFr();
                }}
              />
            </div>

            <div className="col-md-6 mb-3">
              <Form.Label>
                {translate("Numéro de série du reçu") || "Reçu"}
              </Form.Label>
              <Form.Control
                value={recu}
                onChange={(e) => setRecu(e.target.value)}
              />
            </div>

            <div className="col-md-6 mb-3">
              <Form.Label>{translate("Station") || "Station"}</Form.Label>
              <Form.Control
                value={station}
                onChange={(e) => setStation(e.target.value)}
              />
            </div>

            <div className="col-md-6 mb-3">
              <Form.Label>{translate("Date") || "Date"}</Form.Label>
              <Form.Control
                type="datetime-local"
                value={toInputDateTime(date)}
                onChange={(e) => setDate(toServerDateTime(e.target.value))}
              />
            </div>
          </div>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>
          {translate("Close") || "Close"}
        </Button>
        <Button variant="primary" onClick={handleCreate} disabled={saving}>
          {saving
            ? translate("Saving...") || "Saving..."
            : translate("Save") || "Sauvegarder"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

/* ===================== PAGE ===================== */
export default function Carburant() {
  const { translate } = useTranslate();

  const loggedUserId = Number(
    localStorage.getItem("userid") ??
      localStorage.getItem("id_user") ??
      localStorage.getItem("userId") ??
      0,
  );
  const [list, setList] = useState<CarburantRow[]>([]);
  // ✅ selection export (like Vehicles)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const isAllSelected = useMemo(() => {
    if (!list.length) return false;
    return list.every((r) => selectedIds.has(r.id));
  }, [list, selectedIds]);

  const toggleOne = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllCurrentPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allSelected = list.length > 0 && list.every((r) => next.has(r.id));

      if (allSelected) {
        // unselect all current page
        list.forEach((r) => next.delete(r.id));
      } else {
        // select all current page
        list.forEach((r) => next.add(r.id));
      }
      return next;
    });
  };

  // ✅ listUserId = which user receipts you display
  const [listUserId, setListUserId] = useState<number | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  // import
  const [showImport, setShowImport] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);

  //const [list, setList] = useState<CarburantRow[]>([]);
  const [total, setTotal] = useState(0);

  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [limitValue, setLimitValue] = useState(10);

  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<FilterKey>("utilisateur");
  const [selectedSearch, setSelectedSearch] = useState<string>(
    translate("User"),
  );

  const [sortColumn, setSortColumn] = useState<SortColumn>("date");
  const [sortDir, setSortDir] = useState<SortDir>("DESC");

  // modals
  const [showView, setShowView] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [selectedRow, setSelectedRow] = useState<CarburantRow | null>(null);

  const OPTIONS: { key: FilterKey; label: string }[] = useMemo(
    () => [
      { key: "utilisateur", label: translate("User") },
      { key: "vehicule", label: translate("Vehicle") || "Vehicle" },
      { key: "employe", label: translate("Employee") || "Employee" },
      { key: "station", label: translate("Station") || "Station" },
      { key: "carburant", label: translate("Carburant") || "Carburant" },
      { key: "date", label: translate("Date") || "Date" },
    ],
    [translate],
  );

  const filterLabel =
    filter === "utilisateur"
      ? translate("User")
      : filter === "vehicule"
        ? translate("Vehicle") || "Vehicle"
        : filter === "employe"
          ? translate("Employee") || "Employee"
          : filter === "station"
            ? translate("Station") || "Station"
            : filter === "carburant"
              ? translate("Carburant") || "Carburant"
              : translate("Date") || "Date";

  const fetchList = async (opts?: { userId?: number; page?: number }) => {
    const userId = opts?.userId ?? listUserId;
    const page = opts?.page ?? currentPage;

    try {
      setLoading(true);

      const data = await fetchCarburantList({
        id_user: userId,
        page,
        limit: limitValue,
        search,
        filterKey: filter,
        sortColumn,
        sortDir,
      } as any);

      const rows = Array.isArray((data as any)?.data) ? (data as any).data : [];
      const totalRows = Number((data as any)?.total ?? rows.length ?? 0);

      setList(rows);
      setTotal(totalRows);
      setPageCount(Math.ceil(totalRows / limitValue) || 1);
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || "Failed to load carburant");
      setList([]);
      setTotal(0);
      setPageCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    limitValue,
    search,
    filter,
    sortColumn,
    sortDir,
    listUserId,
    reloadTick,
  ]);

  const handleTypeSearch = (event: any) => {
    const selectedValue = event.target.textContent;

    if (selectedValue === translate("User")) setFilter("utilisateur");
    if (selectedValue === (translate("Vehicle") || "Vehicle"))
      setFilter("vehicule");
    if (selectedValue === (translate("Employee") || "Employee"))
      setFilter("employe");
    if (selectedValue === (translate("Station") || "Station"))
      setFilter("station");
    if (selectedValue === (translate("Carburant") || "Carburant"))
      setFilter("carburant");
    if (selectedValue === (translate("Date") || "Date")) setFilter("date");

    setSelectedSearch(selectedValue);
    setCurrentPage(1);
  };

  const handleAdvancedSearch = (event: any) => {
    setSearch(event.target.value);
    setCurrentPage(1);
  };

  const handleResetSearch = () => {
    setSearch("");
    setCurrentPage(1);
  };

  const handleSelectChange = (event: any) => {
    setLimitValue(parseInt(event.target.value, 10));
    setCurrentPage(1);
    window.scrollTo(0, 0);
  };

  const handlePageClick = (data: any) => {
    setCurrentPage(data.selected + 1);
    window.scrollTo(0, 0);
  };

  const handleSortingColumn = (col: SortColumn) => {
    const next = sortDir === "DESC" ? "ASC" : "DESC";
    setSortDir(next);
    setSortColumn(col);
    setCurrentPage(1);
  };

  const handleDelete = async (row: CarburantRow) => {
    const ok = window.confirm(`${translate("Delete")} #${row.id} ?`);
    if (!ok) return;

    try {
      await deleteCarburant(row.id);
      toast.success(translate("Deleted") || "Deleted", {
        position: "bottom-right",
        autoClose: 1500,
      });
      fetchList();
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || translate("Delete failed") || "Delete failed", {
        position: "bottom-right",
        autoClose: 2500,
      });
    }
  };

  const exportCsv = () => {
    const selectedRows = list.filter((r) => selectedIds.has(r.id));

    if (!selectedRows.length) {
      toast.warning("Sélectionnez au moins une ligne à exporter.", {
        position: "bottom-right",
        autoClose: 1800,
      });
      return;
    }

    const headers = [
      "Vehicule",
      "Employe",
      "Utilisateur",
      "Volume",
      "Carburant",
      "Total",
      "Recu",
      "Date",
      "Station",
    ];

    const lines = [
      headers.join(";"),
      ...selectedRows.map((r) =>
        [
          safeText(r.vehicule),
          safeText(r.employe),
          safeText(r.utilisateur),
          safeText(r.volume),
          fuelLabel(r.carburant),
          safeText(r.total),
          safeText(r.recu),
          "\t" + formatDateCell(r.date), // force Excel text display
          safeText(r.station),
        ]
          .map((x) => `"${String(x).replace(/"/g, '""')}"`)
          .join(";"),
      ),
    ];

    const BOM = "\uFEFF";
    const blob = new Blob([BOM + lines.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `carburant_selection_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // ✅ Import helpers
  const validateImportFile = (f: File | null) => {
    if (!f) return null;
    const allowed = [".csv", ".xls", ".xlsx"];
    const name = (f.name || "").toLowerCase();
    if (!allowed.some((ext) => name.endsWith(ext))) {
      toast.error("Format invalide. Utilisez .csv .xls .xlsx");
      return null;
    }
    return f;
  };

  // ✅ Import upload
  const handleImportUpload = async () => {
    if (!importFile) return toast.error("Veuillez sélectionner un fichier.");

    try {
      setImporting(true);
      setImportResult(null);

      const fd = new FormData();
      fd.append("file", importFile);

      const res = await fetch(IMPORT_URL(listUserId ?? loggedUserId), {
        method: "POST",
        body: fd,
        credentials: "include",
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error((data as any)?.message || "Import failed");
      }

      setImportResult(data);

      const inserted = Number((data as any)?.inserted ?? 0);
      const failed = Number((data as any)?.failed ?? 0);

      if (failed > 0)
        toast.warning(
          `Import terminé: ${inserted} ajoutés, ${failed} erreurs.`,
        );
      else toast.success(`Import réussi: ${inserted} lignes ajoutées.`);

      setCurrentPage(1);
      fetchList();
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <>
      <div className="row align-items-center">
        <div className="col-sm-6 col-md-6">
          <h4 style={{ marginBottom: 0 }}>
            <i className="las la-gas-pump"></i>{" "}
            {translate("Carburant") || "Carburant"} ({total})
          </h4>
        </div>

        <div
          className="col-sm-6 col-md-6 d-flex justify-content-end"
          style={{ gap: 10 }}
        >
          <Button
            variant="primary"
            onClick={() => setShowAdd(true)}
            className="d-flex align-items-center"
            style={{ padding: "10px 16px", borderRadius: 4 }}
          >
            <span style={{ fontSize: 18, marginRight: 8, lineHeight: 1 }}>
              +
            </span>
            {translate("Ajouter un reçu") || "Ajouter un reçu"}
          </Button>

          <Button
            variant="outline-secondary"
            onClick={exportCsv}
            disabled={!selectedIds.size}
            className="d-flex align-items-center"
            style={{ padding: "10px 16px", borderRadius: 4 }}
          >
            <i
              className="ri-download-2-line"
              style={{ fontSize: 16, marginRight: 8 }}
            />
            {translate("Exporter") || "Exporter"}
          </Button>

          <Button
            variant="outline-primary"
            onClick={() => {
              setImportFile(null);
              setImportResult(null);
              setShowImport(true);
            }}
            className="d-flex align-items-center"
            style={{ padding: "10px 16px", borderRadius: 4 }}
          >
            <i
              className="ri-upload-2-line"
              style={{ fontSize: 16, marginRight: 8 }}
            />
            {translate("Importer") || "Importer"}
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="row">
        <div
          className="col-md-4"
          style={{ margin: "0px 0px 10px 0px", padding: "10px" }}
        >
          <div className="input-group">
            <div className="dropdown">
              <button
                className="btn btn-link custom-toggle"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i
                  className="fas fa-chevron-down"
                  style={{ fontSize: "20px" }}
                />
              </button>

              <ul className="dropdown-menu" onClick={handleTypeSearch}>
                {OPTIONS.map((opt) => (
                  <li key={opt.key}>
                    <button
                      className={`dropdown-item ${selectedSearch === opt.label ? "active" : ""}`}
                      type="button"
                      onClick={() => setSelectedSearch(opt.label)}
                    >
                      {opt.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <input
              type="text"
              placeholder={`${translate("By")} ${filterLabel}`}
              onChange={handleAdvancedSearch}
              className="form-control"
              value={search}
            />
            <button
              type="button"
              className="btn btn-reset"
              onClick={handleResetSearch}
            >
              <i className="las la-times" style={{ color: "#fff" }}></i>
            </button>
          </div>
        </div>

        <div className="col-md-8 d-flex justify-content-end align-items-center">
          <div className="dataTables_length">
            <label style={{ marginBottom: 0 }}>
              {translate("Show")}
              <select
                className="custom-select custom-select-sm form-control form-control-sm ml-2"
                style={{ width: "66px" }}
                onChange={handleSelectChange}
                value={limitValue}
              >
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
              </select>
            </label>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="row">
        <div className="table-responsive">
          <Table className="dataTable">
            <thead className="bg-white text-uppercase">
              <tr className="ligth ligth-data">
                <th style={{ width: 36 }}>
                  <div className="checkbox d-inline-block">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={isAllSelected}
                      disabled={!list.length}
                      onChange={toggleAllCurrentPage}
                      id="carburant-check-all"
                    />
                    <label
                      htmlFor="carburant-check-all"
                      className="form-check-label mb-0"
                    />
                  </div>
                </th>

                <th
                  className="sorting"
                  onClick={() => handleSortingColumn("vehicule")}
                >
                  {translate("Véhicule") || "Véhicule"}
                </th>
                <th
                  className="sorting"
                  onClick={() => handleSortingColumn("employe")}
                >
                  {translate("Employée") || "Employée"}
                </th>
                <th
                  className="sorting"
                  onClick={() => handleSortingColumn("utilisateur")}
                >
                  {translate("Utilisateur") || "Utilisateur"}
                </th>
                <th
                  className="sorting"
                  onClick={() => handleSortingColumn("volume")}
                >
                  {translate("Volume") || "Volume"}
                </th>
                <th
                  className="sorting"
                  onClick={() => handleSortingColumn("carburant")}
                >
                  {translate("Carburant") || "Carburant"}
                </th>
                <th
                  className="sorting"
                  onClick={() => handleSortingColumn("total")}
                >
                  {translate("Total") || "Total"}
                </th>
                <th
                  className="sorting"
                  onClick={() => handleSortingColumn("recu")}
                >
                  {translate("Reçu") || "Reçu"}
                </th>
                <th
                  className="sorting"
                  onClick={() => handleSortingColumn("date")}
                >
                  {translate("Date") || "Date"}
                </th>
                <th
                  className="sorting"
                  onClick={() => handleSortingColumn("station")}
                >
                  {translate("Station") || "Station"}
                </th>
                <th>{translate("Actions") || "Actions"}</th>
              </tr>
            </thead>

            <tbody className="ligth-body">
              {loading ? (
                <tr style={{ textAlign: "center" }}>
                  <td className="text-center" colSpan={10}>
                    <PropagateLoader
                      color={"#123abc"}
                      loading={loading}
                      size={20}
                    />
                  </td>
                </tr>
              ) : list.length > 0 ? (
                list.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <div className="checkbox d-inline-block">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id={`carburant-check-${row.id}`}
                          checked={selectedIds.has(row.id)}
                          onChange={() => toggleOne(row.id)}
                        />
                        <label
                          htmlFor={`carburant-check-${row.id}`}
                          className="form-check-label mb-0"
                        />
                      </div>
                    </td>

                    <td>{safeText(row.vehicule)}</td>
                    <td>{safeText(row.employe)}</td>
                    <td>{safeText(row.utilisateur)}</td>
                    <td>{safeNumber(row.volume)}</td>
                    <td>{fuelLabel(row.carburant)}</td>
                    <td>{safeNumber(row.total)}</td>
                    <td>{safeText(row.recu)}</td>
                    <td>{formatDateCell(row.date)}</td>
                    <td>{safeText(row.station)}</td>

                    <td style={{ whiteSpace: "nowrap" }}>
                      <a
                        className="badge bg-secondary mr-1"
                        style={{ cursor: "pointer" }}
                        title={translate("View") || "View"}
                        onClick={() => {
                          setSelectedRow(row);
                          setShowView(true);
                        }}
                      >
                        <i className="ri-eye-line" />
                      </a>

                      <a
                        className="badge bg-primary mr-1"
                        style={{ cursor: "pointer" }}
                        title={translate("Edit") || "Edit"}
                        onClick={() => {
                          setSelectedRow(row);
                          setShowEdit(true);
                        }}
                      >
                        <i className="ri-edit-line" />
                      </a>

                      <a
                        className="badge bg-danger"
                        style={{ cursor: "pointer" }}
                        title={translate("Delete") || "Delete"}
                        onClick={() => handleDelete(row)}
                      >
                        <i className="ri-delete-bin-line" />
                      </a>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={10}>
                    {translate("No results") || "Aucun résultat"}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      <div className="row">
        <div className="col-md-6 d-flex align-items-center">
          <span>
            {translate("Displaying")} {list.length} {translate("out of")}{" "}
            {total}
          </span>
        </div>

        <div className="col-md-6">
          <ReactPaginate
            previousLabel={translate("previous")}
            nextLabel={translate("next")}
            breakLabel={"..."}
            pageCount={pageCount}
            marginPagesDisplayed={2}
            pageRangeDisplayed={3}
            onPageChange={handlePageClick}
            containerClassName={"pagination justify-content-end"}
            pageClassName={"page-item"}
            pageLinkClassName={"page-link"}
            previousClassName={"page-item"}
            previousLinkClassName={"page-link"}
            nextClassName={"page-item"}
            nextLinkClassName={"page-link"}
            breakClassName={"page-item"}
            breakLinkClassName={"page-link"}
            activeClassName={"active"}
            forcePage={currentPage - 1}
          />
        </div>
      </div>

      {/* Modals */}
      <ViewCarburantModal
        show={showView}
        onHide={() => setShowView(false)}
        row={selectedRow}
        translate={translate}
      />

      <EditCarburantModal
        show={showEdit}
        onHide={() => setShowEdit(false)}
        row={selectedRow}
        translate={translate}
        onSaved={fetchList}
      />
      <AddCarburantModal
        show={showAdd}
        onHide={() => setShowAdd(false)}
        translate={translate}
        loggedUserId={loggedUserId}
        onCreated={() => {
          setSearch("");
          setCurrentPage(1);

          // ✅ keep listUserId as-is (null => all users)
          fetchList({ page: 1 });
        }}
      />

      {/* IMPORT MODAL */}
      <Modal
        show={showImport}
        onHide={() => setShowImport(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {translate("Importer recharges") || "Importer recharges"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div
            style={{
              border: "2px dashed #cfd4da",
              borderRadius: 10,
              padding: 30,
              textAlign: "center",
              background: "#f8f9fa",
            }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const f = e.dataTransfer.files?.[0] || null;
              const ok = validateImportFile(f);
              if (!ok) return;
              setImportFile(ok);
              setImportResult(null);
            }}
          >
            <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 10 }}>
              {translate(
                "Glisser le fichier ici pour l’envoyer (ou cliquez)",
              ) || "Glisser le fichier ici pour l’envoyer (ou cliquez)"}
            </div>

            <input
              type="file"
              accept=".csv,.xls,.xlsx"
              onChange={(e) => {
                const f = e.target.files?.[0] || null;
                const ok = validateImportFile(f);
                if (!ok) {
                  e.currentTarget.value = "";
                  return;
                }
                setImportFile(ok);
                setImportResult(null);
              }}
            />

            <div className="mt-3 text-muted" style={{ fontSize: 13 }}>
              {translate("Types de fichier") || "Types de fichier"} : CSV, XLS,
              XLSX
            </div>

            {importFile && (
              <div className="mt-3">
                <span className="badge bg-primary">{importFile.name}</span>
              </div>
            )}
          </div>

          {importResult && (
            <div
              className="mt-4"
              style={{
                background: "#fff",
                borderRadius: 10,
                padding: 15,
                border: "1px solid #eee",
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 8 }}>
                Résultat import
              </div>
              <div>Total lignes: {(importResult as any).total}</div>
              <div>Insérées: {(importResult as any).inserted}</div>
              <div>Erreurs: {(importResult as any).failed}</div>

              {(importResult as any)?.errors?.length ? (
                <div className="mt-3">
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>
                    Détails erreurs (max 200)
                  </div>
                  <div
                    style={{ maxHeight: 180, overflow: "auto", fontSize: 13 }}
                  >
                    {(importResult as any).errors.map(
                      (er: any, idx: number) => (
                        <div key={idx} className="text-danger">
                          Ligne {er.row}: {er.message}
                        </div>
                      ),
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowImport(false)}
            disabled={importing}
          >
            {translate("Annuler") || "Annuler"}
          </Button>
          <Button
            variant="primary"
            onClick={handleImportUpload}
            disabled={importing || !importFile}
          >
            {importing
              ? translate("Import...") || "Import..."
              : translate("Importer") || "Importer"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
